import React, { FunctionComponent, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckupRequestInfo } from '@companion-professional/dctypes';
import { VideoCaptureUploader } from '../../components/VideoCaptureUploader';
import { InBrowserCamera  } from '@companion-professional/components';
import { NeedHelpContactSupport } from '../../components/NeedHelpContactSupport';
import { trackEvent } from '../../lib/trackEvent';
import landscape_warning from '../../assets/landscape_warning.png';

interface CaptureOrUploadProps {
  // checkupRequest is the checkup request that is being fulfilled.
  checkupRequest: CheckupRequestInfo;
}

// CaptureOrUpload is responsible for handling both video capture and file upload based on the state of the videoFile.
// It maintains the same UI and behavior as the original Checkup component but is now a separate page.
export const CaptureOrUpload: FunctionComponent<CaptureOrUploadProps> = ({ checkupRequest }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get file from navigation state or URL params
  const [videoFile, setVideoFile] = useState<File | null>(() => {
    // Try to get file from navigation state first
    if (location.state?.file) {
      return location.state.file;
    }
    // Could also get from URL params if needed
    return null;
  });

  return (
    <div className="flex w-full flex-1 flex-col justify-between pb-4">
      <div className="flex w-full flex-1 flex-col items-center">
        { /* If we go to this component with videoFile non-null, it means we are uploading a video file
          otherwise we are capturing a video in the browser, and uploading the video to mux afterwards */}
        { videoFile ? (
          <VideoCaptureUploader
            itemId={checkupRequest.items?.[0]?.item_id}
            file={videoFile}
            onReset={() => {
            setVideoFile(null);
            }}
            onUploadComplete={() => {
              trackEvent('upload_video_completed');
              {/* Once the video is uploaded, we can consider this checkup complete so navigate to the complete page.
              This is important when we come to complete page, and user clicks back button
              they should be redirected to the instructions page, or the last customized page sent by the backend.
              there we use replace: true to replace the current history entry so browser back button skips this page */}
              navigate('../complete', { replace: true });
            }}
            onUploadStart={() => {
              trackEvent('upload_video_started');
            }}
          />
        ) : (  
            <InBrowserCamera
                onUploadVideo={(file: File) => {
                  setVideoFile(file);
                }}
                onError={(error) => {
                    console.error('Error recording video', error);
                    {{/* TODO: SHOW a popup asking users to upload a video file instead!!!!!! */}}
                    navigate(-1);
                }}
                onBack={() => {
                  {{/* Navigate back to the previous page, commonly known as instructions page,
                    but we won't know the exact page because it's dynamic. */
                  }}
                    navigate(-1);
                }}
           
                id = {checkupRequest.items?.[0]?.item_id ?? ''}
                warningImage={landscape_warning}
                emitBrowserCameraMetrics={(properties) => {
                  trackEvent('checkup_request_in_browser_camera_metrics', {
                    id: properties.id,
                    duration: properties.duration,
                    number_of_short_videos_recorded: properties.number_of_short_videos_recorded,
                    number_of_times_videos_capture_clicked: properties.number_of_times_videos_capture_clicked,
                  });
                }}
            />
        )
      }
      <NeedHelpContactSupport />
    </div>
    </div>
  );
};
